
import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { Button } from '@/components/ui/button';
import { UserPlus } from 'lucide-react';
import { AddUserDialog } from './AddUserDialog';
import { UsersTableContent } from './UsersTableContent';
import { useAuth } from '@/contexts/AuthContext';

// Define the UserRole interface - password removed for security
export interface UserRole {
  id: string;
  user_email: string;
  role: 'admin' | 'user';
  created_at: string;
  user_id?: string;
}

export const UserTable = () => {
  const [isAddUserDialogOpen, setIsAddUserDialogOpen] = useState(false);
  const { toast } = useToast();
  const { user, profile } = useAuth();

  // Fetch users
  const { data: users = [], isLoading, error } = useQuery({
    queryKey: ['user-roles'],
    queryFn: async () => {
      console.log('Fetching user roles...');
      
      try {
        const { data, error } = await supabase
          .from('user_roles')
          .select('*')
          .order('created_at', { ascending: false });

        if (error) {
          console.error('Error fetching user roles:', error);
          toast({
            title: "Error fetching users",
            description: error.message,
            variant: "destructive",
          });
          throw error;
        }

        console.log('Successfully fetched user roles:', data);
        return data as UserRole[];
      } catch (error) {
        console.error('Exception while fetching user roles:', error);
        throw error;
      }
    },
  });

  // If there's an error, we still want to render the component rather than crashing
  if (error) {
    console.error('Error in user roles query:', error);
  }

  // Check if the current user is an admin
  const isAdmin = profile?.role === 'admin';

  return (
    <div className="w-full">
      <div className="flex justify-between items-center mb-4">
        <h2 className="text-2xl font-bold">User Management</h2>
        {isAdmin && (
          <Button 
            onClick={() => setIsAddUserDialogOpen(true)}
            className="bg-primary hover:bg-primary/80"
          >
            <UserPlus className="h-4 w-4 mr-2" />
            Add User
          </Button>
        )}
      </div>

      <UsersTableContent users={users} isLoading={isLoading} error={error} />
      
      <AddUserDialog 
        isOpen={isAddUserDialogOpen} 
        onOpenChange={setIsAddUserDialogOpen} 
      />
    </div>
  );
};
